import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const CreateServices = () => {
  const [formData, setFormData] = useState({
    service: "",
    service_subtype: "",
    label_on_bill: "",
    unit_cost: "",
    facility_type: "",
    facility_id: "",
  });

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const accessToken = getDecryptedAccessToken();

  // **Fetch Facility Data**
  useEffect(() => {
    ApiServices
      .get("/apis/default/api/facility", {
        Authorization: `Bearer ${accessToken}`,
      })
      .then((response) => {
        if (response.data?.data) {
          setFacilities(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching facilities:", error);
        setSnackbar({ open: true, message: "Failed to load facilities.", severity: "error" });
      });
  }, []);

  // **Handle Input Change**
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // **Handle Facility Selection**
  const handleFacilitySelect = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        facility_type: newValue.facility_type,
        facility_id: newValue.id,
      });
    }
  };

  // **Handle Form Submission**
  const handleSubmit = async () => {
    // Check for required fields
    if (!formData.service || !formData.label_on_bill || !formData.unit_cost || !formData.facility_id) {
      setSnackbar({ open: true, message: "All required fields must be filled!", severity: "error" });
      return;
    }

    const payload = {
      service_no: 41, // Static for now
      service: formData.service,
      service_subtype: formData.service_subtype || "", // Optional field
      label_on_bill: formData.label_on_bill,
      unit_cost: parseFloat(formData.unit_cost),
      facility_type: formData.facility_type,
      facility_id: formData.facility_id,
    };

    setLoading(true);
    try {
      const response = await ApiServices.post(
        "/apis/default/api/masterServices",
        payload, {
          Authorization: `Bearer ${accessToken}`,
        }
      );

      setSnackbar({ open: true, message: "Service added successfully!", severity: "success" });

      // Reset Form
      setFormData({
        service: "",
        service_subtype: "",
        label_on_bill: "",
        unit_cost: "",
        facility_type: "",
        facility_id: "",
      });
    } catch (error) {
      console.error("Error creating service:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to add service.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxWidth: 400,
          width: "100%",
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        {/* Service Name */}
        <TextField label="Service Name *" name="service" value={formData.service} onChange={handleChange} fullWidth required />

        {/* Service Subtype (Optional) */}
        <TextField label="Service Subtype" name="service_subtype" value={formData.service_subtype} onChange={handleChange} fullWidth />

        {/* Label on Bill */}
        <TextField label="Label on Bill *" name="label_on_bill" value={formData.label_on_bill} onChange={handleChange} fullWidth required />

        {/* Unit Cost */}
        <TextField
          label="Unit Cost *"
          name="unit_cost"
          type="number"
          value={formData.unit_cost}
          onChange={handleChange}
          fullWidth
          required
        />

        {/* Facility Selection */}
        <Autocomplete
          options={facilities}
          getOptionLabel={(option) => option.name}
          onChange={handleFacilitySelect}
          renderInput={(params) => <TextField {...params} label="Select Facility *" fullWidth required />}
          loading={facilities.length === 0}
        />

        {/* Submit Button */}
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Create Service"}
        </Button>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateServices;
